import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from 'components/foundations/Icon';
import css from './LogoUpload.css';

const translationPrefix = 'STATIC.PAGES.EXTERNAL_ORG_PROFILE';
const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_LOGO_SIZE_MB = MAX_LOGO_SIZE_BYTES / (1024 * 1024);
const ACCEPTED_LOGO_MIME_TYPES = ['image/png', 'image/jpeg'];
const ACCEPTED_LOGO_MIME_TYPES_TEXT = ACCEPTED_LOGO_MIME_TYPES.map(type =>
    type.replace('image/', '').toUpperCase()
).join(' or ');

const LogoUpload = ({
    logoFile = null,
    logoUrl = '',
    error = '',
    onFileChange,
    onValidationError,
    onRemove,
    translate,
}) => {
    const fileInputRef = useRef(null);
    const hasLogo = Boolean(logoFile || logoUrl);
    const fileName = logoFile?.name || '';

    const setValidationError = (errorKey, data = undefined) => {
        onValidationError(translate(`${translationPrefix}.${errorKey}`, data));
    };

    const handleChange = async event => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > MAX_LOGO_SIZE_BYTES) {
            setValidationError('MESSAGES.LOGO_FILE_SIZE_ERROR', { maxMb: MAX_LOGO_SIZE_MB });
            event.target.value = '';
            return;
        }

        if (!ACCEPTED_LOGO_MIME_TYPES.includes(file.type)) {
            setValidationError('MESSAGES.LOGO_INVALID_ERROR', { types: ACCEPTED_LOGO_MIME_TYPES_TEXT });
            event.target.value = '';
            return;
        }

        onValidationError('');
        onFileChange(file);
        event.target.value = '';
    };

    const handleAreaClick = () => fileInputRef.current?.click();

    const handleRemove = e => {
        e.stopPropagation();
        onRemove();
    };

    return (
        <div className={css.wrapper}>
            <div
                role="button"
                tabIndex={0}
                className={css.uploadArea}
                onClick={handleAreaClick}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') handleAreaClick();
                }}
                data-has-logo={hasLogo || undefined}
            >
                {hasLogo ? (
                    <>
                        <div className={css.logoWrapper}>
                            <img
                                src={logoFile ? URL.createObjectURL(logoFile) : logoUrl}
                                alt={translate(`${translationPrefix}.LOGO_LABEL`)}
                                className={css.logoPreview}
                            />
                            <Icon
                                name="x"
                                size={IconSizes.X_SMALL}
                                variant={IconVariants.WHITE}
                                onClick={handleRemove}
                                className={css.removeButton}
                                aria-label="Remove logo"
                            />
                        </div>
                        {fileName && <span className={css.fileName}>{fileName}</span>}
                    </>
                ) : (
                    <span className={css.uploadText}>
                        <Translate id={`${translationPrefix}.LOGO_NO_LOGO`} />
                    </span>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_LOGO_MIME_TYPES.join(',')}
                    style={{ display: 'none' }}
                    onChange={handleChange}
                    onClick={e => {
                        e.target.value = '';
                    }}
                />
            </div>
            {!!error && <p className={css.errorText}>{error}</p>}
        </div>
    );
};

LogoUpload.propTypes = {
    logoFile: PropTypes.instanceOf(File),
    logoUrl: PropTypes.string,
    error: PropTypes.string,
    onFileChange: PropTypes.func.isRequired,
    onValidationError: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(LogoUpload);
